import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, ArrowRight, AlertCircle } from 'lucide-react';

export default function RegisterPage() {
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    const result = await register(email, password, name);
    if (!result.success) {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="register-page">
      {/* Left: Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Conference room"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <h1 className="font-heading text-4xl font-bold tracking-tighter text-white mb-3">
            mr.polygon.rs
          </h1>
          <p className="text-text-secondary text-base font-body max-w-md">
            Create your account and start booking resources instantly.
          </p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10">
            <h1 className="font-heading text-2xl font-bold tracking-tighter text-white">mr.polygon.rs</h1>
            <p className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary mt-1">/bookings</p>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-2xl font-medium tracking-tight text-white" data-testid="register-heading">Create account</h2>
            <p className="text-text-secondary text-sm mt-2 font-body">Set up your profile to start booking.</p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 mb-6 border border-booked/30 bg-booked/10 text-sm text-booked" data-testid="register-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Full name</label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="register-name-input"
                  required
                  className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                  placeholder="Jane Smith"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  data-testid="register-email-input"
                  required
                  className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary block mb-2">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="register-password-input"
                  required
                  minLength={6}
                  className="w-full bg-surface border border-border text-white pl-10 pr-4 py-2.5 text-sm font-body placeholder:text-text-secondary/50 focus:ring-2 focus:ring-accent focus:border-transparent outline-none transition-all"
                  placeholder="Min 6 characters"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-white text-black font-body font-medium text-sm py-2.5 flex items-center justify-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
              ) : (
                <>Create account <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="text-text-secondary text-sm mt-6 text-center font-body">
            Already have an account?{' '}
            <Link to="/login" className="text-white hover:underline" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
